import { ChangeDetectionStrategy, Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { ModalHelperService } from '../../../../../../services/modal-helper.service';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { PermissionScope } from '../../../../../../types/permission-scope.enum';
import { assetWriteSignal } from '../../../../../../utils/asset-permissions.utils';
import { assetIdSignalFromRoute } from '../../../../../../utils/asset-route.utils';
import { CampaignPlanningComponent, PlanningDialogData } from '../planning/campaign-planning.component';
import { openCampaignPlanningDialog } from '../planning/open-planning-dialog.util';

// Hosts the planning grid on the model's snapshot campaign. The grid is shown planned-only
// (the model's actual content); the catalogue is reached through the "Planifier" dialog, the
// same proven path real campaigns use. The grid mutates the snapshot directly, so every change
// updates what the model will apply.
@Component({
  selector: 'app-campaign-model-planning-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CampaignPlanningComponent],
  providers: [DialogService],
  template: `
    @if (campaignId(); as campaignId) {
      <app-campaign-planning
        [campaignId]="campaignId"
        [assetId]="assetId()"
        [mode]="mode"
        [readOnly]="true"
        [defaultPlannedOnly]="true" />
    }
  `,
})
export class CampaignModelPlanningTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly dialogService = inject(DialogService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly translate = inject(TranslocoService);

  // Surfaced to the detail page header, which hosts the "Planifier" action and the counter.
  readonly planning = viewChild(CampaignPlanningComponent);

  readonly mode = this.route.snapshot.data['mode'] as PlanningDialogData['mode'];
  readonly assetId = assetIdSignalFromRoute(this.route);
  readonly canWriteAsset = assetWriteSignal(PermissionScope.Campaigns, this.assetId);
  readonly campaignId = signal<number | null>(null);

  ngOnInit(): void {
    void this.resolveSnapshotCampaign();
  }

  // Opens the full catalogue in a dialog to add/remove measures; the planned-only list refreshes
  // on close. Mirrors CesCampaignMeasuresComponent.openPlanningDialog so both worlds share the path.
  openPlanningDialog(): void {
    if (!this.canWriteAsset()) return;
    const campaignId = this.campaignId();
    const assetId = this.assetId();
    if (campaignId == null || assetId == null) return;

    openCampaignPlanningDialog(
      { dialogService: this.dialogService, modalHelper: this.modalHelper, translate: this.translate },
      { campaignId, assetId, mode: this.mode },
      () => void this.planning()?.reload(),
    );
  }

  private async resolveSnapshotCampaign(): Promise<void> {
    const modelId = Number(this.route.snapshot.paramMap.get('modelId'));
    if (!modelId) return;
    const model = await this.campaignModelStore.getByIdAsync(modelId);
    if (model) this.campaignId.set(model.campaignId);
  }
}
